import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { MailCheck, ShieldAlert, Loader2, Sparkles, ArrowRight, Pencil, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get("token");
  const emailParam = params.get("email") || "";
  const [status, setStatus] = useState(token ? "verifying" : "info");
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [devUrl, setDevUrl] = useState(location.state?.devVerifyUrl || "");
  const [pv, setPv] = useState(null);          // pre-verification limited-session status
  const [saved, setSaved] = useState([]);      // the user's own saved public-tool result(s)
  const [nextStep, setNextStep] = useState(null);
  const [correcting, setCorrecting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [correctMsg, setCorrectMsg] = useState("");

  const runVerify = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      setUser(data);
      setStatus("success");
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  }, [token, setUser, navigate]);

  useEffect(() => {
    if (token) runVerify();
  }, [token, runVerify]);

  // Load the scoped pre-verification experience (own saved result + next steps) if a
  // limited session exists (set at registration). Silently falls back if not present.
  useEffect(() => {
    if (token) return;
    (async () => {
      try {
        const { data } = await api.get("/preverify/status");
        setPv(data);
        try { const r = await api.get("/preverify/saved-result"); setSaved(r.data?.results || []); } catch (e) { /* ignore */ }
        try { const n = await api.get("/preverify/next-step"); setNextStep(n.data); } catch (e) { /* ignore */ }
      } catch (e) {
        // Already verified but still holding a stale limited cookie → send them to sign in.
        if (e.response?.status === 409) navigate("/login");
        /* otherwise no limited session — basic info mode */
      }
    })();
  }, [token]);

  const resend = async () => {
    setResendMsg("");
    try {
      const { data } = pv
        ? await api.post("/preverify/resend", {})
        : await api.post("/auth/resend-verification", { email: emailParam });
      if (data?.dev_verification_url) setDevUrl(data.dev_verification_url);
      setResendMsg(data?.email_status === "failed"
        ? (data?.email_error || "Email delivery failed. Please try again shortly.")
        : (data?.message || "Verification email sent."));
      setResent(true);
    } catch (err) {
      setResendMsg(formatApiError(err.response?.data?.detail) || "Couldn't resend right now. Please try again.");
    }
  };

  const correctEmail = async () => {
    setCorrectMsg("");
    try {
      const { data } = await api.post("/preverify/correct-email", { new_email: newEmail });
      setPv((p) => ({ ...(p || {}), email: data.email }));
      setCorrectMsg(data?.message || "Email updated.");
      setCorrecting(false);
      setNewEmail("");
    } catch (err) {
      setCorrectMsg(formatApiError(err.response?.data?.detail) || "Couldn't update your email right now.");
    }
  };

  const DevLink = () => devUrl ? (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 mb-4" data-testid="dev-verify-link">
      <p className="text-xs text-amber-400 mb-2">Email delivery isn't configured yet (log-only mode). Use this link to verify:</p>
      <a href={devUrl} data-testid="dev-verify-anchor" className="text-sm text-[#D4AF37] hover:underline break-all">{devUrl}</a>
    </div>
  ) : null;

  if (status === "verifying") {
    return (
      <AuthShell title="Verifying your email" subtitle="Hang tight for a moment.">
        <div className="flex items-center gap-3 text-[#A1A1AA]" data-testid="verify-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" /> Confirming your account…
        </div>
      </AuthShell>
    );
  }

  if (status === "success") {
    return (
      <AuthShell title="Email verified" subtitle="Redirecting you to your dashboard…">
        <div className="flex items-center gap-3 text-emerald-400" data-testid="verify-success">
          <MailCheck className="w-6 h-6" /> You're all set. Welcome to Fairview Systems.
        </div>
      </AuthShell>
    );
  }

  if (status === "error") {
    return (
      <AuthShell title="Verification failed" subtitle="This link is invalid or has expired.">
        <div className="flex items-start gap-3 text-red-400 mb-6" data-testid="verify-error">
          <ShieldAlert className="w-6 h-6 shrink-0" /> <span>{error}</span>
        </div>
        {emailParam && (
          <Button onClick={resend} data-testid="verify-resend-btn"
            className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90 mb-3">
            {resent ? "Resend again" : "Resend verification email"}
          </Button>
        )}
        {resendMsg && <p className="text-sm text-[#D4D4D8] mb-3" data-testid="verify-resend-msg">{resendMsg}</p>}
        <DevLink />
        <Link to="/login" className="text-[#D4AF37] hover:underline text-sm" data-testid="verify-goto-login">Back to sign in</Link>
      </AuthShell>
    );
  }

  // info mode — arrived here right after registering (scoped pre-verification experience)
  const shownEmail = (pv && pv.email) || emailParam;
  return (
    <AuthShell title="You're almost in" subtitle="One quick step to activate your account.">
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 mb-5" data-testid="verify-info">
        <div className="flex items-center gap-2 text-emerald-400 mb-2">
          <CheckCircle2 className="w-5 h-5" /> <span className="text-sm font-medium">Account created</span>
        </div>
        <p className="text-[#D4D4D8] leading-relaxed text-sm">
          We sent a verification link{shownEmail ? <> to <span className="text-white font-medium" data-testid="verify-email-address">{shownEmail}</span></> : ""}.
          Click it to verify your email and unlock your full command center.
        </p>
      </div>

      {saved.length > 0 && (
        <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5 mb-5" data-testid="preverify-saved-result">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
            <Sparkles className="w-4 h-4" /> <span className="text-sm font-medium">Your saved result is waiting</span>
          </div>
          <p className="text-white text-sm font-medium mb-1" data-testid="preverify-saved-title">{saved[0].title}</p>
          <p className="text-[#A1A1AA] text-xs leading-relaxed line-clamp-3 whitespace-pre-wrap">
            {(saved[0].content || "").slice(0, 220)}{(saved[0].content || "").length > 220 ? "…" : ""}
          </p>
          <p className="text-[#71717A] text-xs mt-2">Verify your email to keep it in your workspace.</p>
        </div>
      )}

      {nextStep?.steps?.length > 0 && (
        <div className="rounded-xl border border-white/8 bg-[#121214] p-5 mb-5" data-testid="preverify-next-step">
          <p className="text-[#D4D4D8] text-sm font-medium mb-3">What happens next</p>
          <ol className="space-y-2.5">
            {nextStep.steps.map((s, i) => (
              <li key={s.key} className="flex items-start gap-3" data-testid={`preverify-step-${s.key}`}>
                <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                <div>
                  <p className="text-white text-sm">{s.title}</p>
                  <p className="text-[#71717A] text-xs">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      <DevLink />
      <Button onClick={resend} data-testid="verify-resend-btn"
        className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90 mb-3">
        {resent ? "Resend again" : "Resend verification email"}
      </Button>
      {resendMsg && <p className="text-sm text-[#D4D4D8] mb-3 text-center" data-testid="verify-resend-msg">{resendMsg}</p>}

      {pv && (
        <div className="mb-3" data-testid="preverify-correct-email">
          {!correcting ? (
            <button type="button" onClick={() => { setCorrecting(true); setCorrectMsg(""); }}
              data-testid="preverify-correct-email-toggle"
              className="w-full flex items-center justify-center gap-1.5 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Entered the wrong email? Fix it here
            </button>
          ) : (
            <div className="rounded-xl border border-white/8 bg-[#121214] p-4 space-y-3">
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                data-testid="preverify-new-email-input" placeholder="your.correct@email.com"
                className="bg-[#0A0A0B] border-white/10 text-white h-11" />
              <div className="flex gap-2">
                <Button onClick={correctEmail} disabled={!newEmail} data-testid="preverify-correct-email-submit"
                  className="flex-1 fv-gold-gradient text-[#0A0A0B] font-semibold h-10 rounded-lg hover:opacity-90 disabled:opacity-50">
                  Update & resend <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Button onClick={() => { setCorrecting(false); setNewEmail(""); }} data-testid="preverify-correct-email-cancel"
                  className="bg-white/5 text-[#D4D4D8] h-10 rounded-lg hover:bg-white/10 border border-white/10">
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {correctMsg && <p className="text-sm text-[#D4D4D8] mt-2 text-center" data-testid="preverify-correct-email-msg">{correctMsg}</p>}
        </div>
      )}

      <p className="text-center text-sm text-[#A1A1AA]">
        Already verified? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="verify-goto-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
